import { normalizeEffectBehaviorSummary } from "@granular-software/metamodel-effect-behaviors";
import {
  createFeedPublisher,
  type FeedPublishTransport,
  type FeedPublisher,
  type TransientFeedbackHandle,
} from "./feed";
import type {
  EffectVersionSelector,
  EffectHandlerContext,
  EffectInvocationMode,
  ManifestEffectMetamodelSpec,
  ResolvedEffectBehaviors,
  ToolWithHandler,
} from "./types";

type EffectRuntimeRequest = {
  effectKey: string;
  effectName: string;
  input: unknown;
  context?: EffectHandlerContext;
};

export interface EffectRuntimeFeedbackBridge {
  /** Ephemeral registry capability for this one active invocation. */
  invocationId: string;
  publish: FeedPublishTransport;
}

export interface InvokeRegisteredEffectOptions {
  feedback?: EffectRuntimeFeedbackBridge;
}

type ResolvedHandler = {
  effect: ToolWithHandler;
  mode: EffectInvocationMode;
  handler: ToolWithHandler["handler"];
};

function computeEffectKey(effect: {
  name: string;
  className?: string | null;
  static?: boolean;
}): string {
  const attachedClass = effect.className?.trim();
  if (!attachedClass) {
    return `global:${effect.name}`;
  }
  return effect.static
    ? `class:${attachedClass}:static:${effect.name}`
    : `class:${attachedClass}:instance:${effect.name}`;
}

function computeEffectVersionSelectorSpecificity(
  selector?: EffectVersionSelector,
): number {
  if (!selector || selector.mode === "all") {
    return 0;
  }
  if (selector.mode === "exact") {
    return 2;
  }
  return 1;
}

function matchesEffectVersionSelector(
  selector: EffectVersionSelector | undefined,
  buildVersionNumber: number | undefined,
): boolean {
  if (!selector || selector.mode === "all") {
    return true;
  }
  if (
    typeof buildVersionNumber !== "number" ||
    !Number.isFinite(buildVersionNumber)
  ) {
    return false;
  }
  if (selector.mode === "exact") {
    return buildVersionNumber === selector.versionNumber;
  }
  if (selector.mode === "before") {
    return buildVersionNumber < selector.versionNumber;
  }
  return buildVersionNumber > selector.versionNumber;
}

function selectRegisteredEffect(
  effectMap: Map<string, ToolWithHandler>,
  effectKey: string,
  buildVersionNumber?: number,
): ToolWithHandler | undefined {
  let bestEffect: ToolWithHandler | undefined;
  let bestSpecificity = Number.NEGATIVE_INFINITY;

  for (const effect of effectMap.values()) {
    if (computeEffectKey(effect) !== effectKey) {
      continue;
    }
    if (
      !matchesEffectVersionSelector(effect.versionSelector, buildVersionNumber)
    ) {
      continue;
    }

    const specificity = computeEffectVersionSelectorSpecificity(
      effect.versionSelector,
    );
    if (!bestEffect || specificity > bestSpecificity) {
      bestEffect = effect;
      bestSpecificity = specificity;
    }
  }

  return bestEffect;
}

export function normalizeEffectBehaviors(
  value?: ManifestEffectMetamodelSpec | ResolvedEffectBehaviors | null,
): ResolvedEffectBehaviors {
  return (normalizeEffectBehaviorSummary(
    value as Record<string, unknown> | null | undefined,
  ) || {}) as ResolvedEffectBehaviors;
}

function resolveInvocationMode(
  context?: EffectHandlerContext,
): EffectInvocationMode {
  const mode = context?.invocation?.mode;
  if (mode === "dryRun" || mode === "reverse" || mode === "artifactOptions") {
    return mode;
  }
  return "execute";
}

function resolveReverseEffect(
  effectMap: Map<string, ToolWithHandler>,
  currentEffect: ToolWithHandler,
  request: EffectRuntimeRequest,
  behaviors: ResolvedEffectBehaviors,
): ToolWithHandler | undefined {
  const explicitReverseHandler =
    request.context?.invocation?.reverseHandler?.trim();
  const configuredReverseHandler = behaviors.reverse?.handler?.trim();
  const reverseHandler = explicitReverseHandler || configuredReverseHandler;
  if (!reverseHandler) {
    return undefined;
  }

  if (reverseHandler.includes(":")) {
    return selectRegisteredEffect(
      effectMap,
      reverseHandler,
      request.context?.buildVersionNumber,
    );
  }

  const directMatch = selectRegisteredEffect(
    effectMap,
    reverseHandler,
    request.context?.buildVersionNumber,
  );
  if (directMatch) {
    return directMatch;
  }

  const candidateKeys = [
    computeEffectKey({
      name: reverseHandler,
      className: currentEffect.className,
      static: currentEffect.static,
    }),
    computeEffectKey({
      name: reverseHandler,
    }),
  ];

  for (const candidateKey of candidateKeys) {
    const candidate = selectRegisteredEffect(
      effectMap,
      candidateKey,
      request.context?.buildVersionNumber,
    );
    if (candidate) {
      return candidate;
    }
  }

  return undefined;
}

function resolveHandlerForMode(
  effectMap: Map<string, ToolWithHandler>,
  effect: ToolWithHandler,
  request: EffectRuntimeRequest,
): ResolvedHandler {
  const behaviors = normalizeEffectBehaviors(
    request.context?.behaviors || effect.metamodels || undefined,
  );
  const mode = resolveInvocationMode(request.context);

  if (mode === "artifactOptions") {
    if (!effect.artifactOptionsHandler) {
      throw new Error(
        `Artifact relationship options are not supported for ${request.effectKey}`,
      );
    }
    return {
      effect,
      mode,
      handler: effect.artifactOptionsHandler,
    };
  }

  if (mode === "dryRun") {
    if (effect.dryRunHandler) {
      return { effect, mode, handler: effect.dryRunHandler };
    }
    if (behaviors.dryRun?.enabled) {
      return { effect, mode, handler: effect.handler };
    }
    throw new Error(`Dry run is not supported for ${request.effectKey}`);
  }

  if (mode === "reverse") {
    if (effect.reverseHandler) {
      return { effect, mode, handler: effect.reverseHandler };
    }

    const reverseEffect = resolveReverseEffect(
      effectMap,
      effect,
      request,
      behaviors,
    );
    if (reverseEffect) {
      return {
        effect: reverseEffect,
        mode,
        handler: reverseEffect.reverseHandler || reverseEffect.handler,
      };
    }

    throw new Error(
      `Reverse execution is not supported for ${request.effectKey}`,
    );
  }

  return { effect, mode, handler: effect.handler };
}

function createInvocationFeedbackContext(bridge: EffectRuntimeFeedbackBridge): {
  feedback: FeedPublisher["feedback"];
  transientFeedback: FeedPublisher["transientFeedback"];
  flush: () => Promise<void>;
} {
  const publications: Promise<unknown>[] = [];
  let nextOperationOrdinal = 0;
  const nextOperationId = (kind: string): string =>
    `${kind}:${nextOperationOrdinal++}`;
  const track = <T>(publication: Promise<T>): Promise<T> => {
    const tracked = Promise.resolve(publication);
    publications.push(tracked);
    // Flush observes every tracked rejection. Attach a handler immediately so
    // an intentionally unawaited publication cannot trigger a process-level
    // unhandled-rejection warning before the effect handler returns.
    void tracked.catch(() => undefined);
    return tracked;
  };
  const publisher = createFeedPublisher((method, params) =>
    bridge.publish(method, {
      ...params,
      invocationId: bridge.invocationId,
    }),
  );

  const wrapTransientHandle = (
    initial: TransientFeedbackHandle,
  ): TransientFeedbackHandle => {
    let current = initial;
    const wrapped: TransientFeedbackHandle = {
      get id() {
        return current.id;
      },
      get ordinal() {
        return current.ordinal;
      },
      get revision() {
        return current.revision;
      },
      async update(text, options = {}) {
        current = await track(
          current.update(text, {
            ...options,
            operationId:
              options.operationId || nextOperationId("transient-update"),
          }),
        );
        return wrapped;
      },
      settle(text, options = {}) {
        return track(
          current.settle(text, {
            ...options,
            operationId:
              options.operationId || nextOperationId("transient-settle"),
          }),
        );
      },
    };
    return wrapped;
  };

  return {
    feedback(text, options = {}) {
      return track(
        publisher.feedback(text, {
          ...options,
          operationId: options.operationId || nextOperationId("feedback"),
        }),
      );
    },
    transientFeedback(text, options = {}) {
      return track(
        publisher
          .transientFeedback(text, {
            ...options,
            operationId:
              options.operationId || nextOperationId("transient-create"),
          })
          .then(wrapTransientHandle),
      );
    },
    async flush() {
      let cursor = 0;
      let firstError: unknown;
      // A settled create can schedule an update while an earlier batch is
      // being awaited, so continue until no new publication was registered.
      while (cursor < publications.length) {
        const batch = publications.slice(cursor);
        cursor = publications.length;
        const results = await Promise.allSettled(batch);
        for (const result of results) {
          if (result.status === "rejected" && firstError === undefined) {
            firstError = result.reason;
          }
        }
      }
      if (firstError !== undefined) {
        throw firstError;
      }
    },
  };
}

export async function invokeRegisteredEffect(
  effectMap: Map<string, ToolWithHandler>,
  request: EffectRuntimeRequest,
  options: InvokeRegisteredEffectOptions = {},
): Promise<unknown> {
  const effect = selectRegisteredEffect(
    effectMap,
    request.effectKey,
    request.context?.buildVersionNumber,
  );
  if (!effect) {
    throw new Error(`Effect handler not found: ${request.effectKey}`);
  }

  const resolved = resolveHandlerForMode(effectMap, effect, request);
  const context: EffectHandlerContext = {
    ...(request.context || {}),
    behaviors: normalizeEffectBehaviors(
      request.context?.behaviors || effect.metamodels || undefined,
    ),
    invocation: {
      mode: resolved.mode,
      sourceEffectKey: request.effectKey,
      sourceEffectName: request.effectName,
      ...(request.context?.invocation?.reverseHandler
        ? { reverseHandler: request.context.invocation.reverseHandler }
        : {}),
      ...(request.context?.invocation?.artifactId
        ? { artifactId: request.context.invocation.artifactId }
        : {}),
      ...(request.context?.invocation?.artifactOptions
        ? { artifactOptions: request.context.invocation.artifactOptions }
        : {}),
    },
  } as EffectHandlerContext;
  const feedbackContext = options.feedback
    ? createInvocationFeedbackContext(options.feedback)
    : null;
  if (feedbackContext) {
    context.feedback = feedbackContext.feedback;
    context.transientFeedback = feedbackContext.transientFeedback;
  }

  let handlerResult: unknown;
  let handlerError: unknown;
  let handlerFailed = false;
  try {
    if (resolved.mode === "artifactOptions") {
      handlerResult = await (resolved.handler as any)(request.input, context);
    } else if (
      resolved.effect.className &&
      !resolved.effect.static &&
      request.input &&
      typeof request.input === "object" &&
      "_objectId" in (request.input as Record<string, unknown>)
    ) {
      const { _objectId, ...rest } = request.input as Record<
        string,
        unknown
      > & {
        _objectId: string;
      };
      handlerResult = await (resolved.handler as any)(_objectId, rest, context);
    } else {
      handlerResult = await (resolved.handler as any)(request.input, context);
    }
  } catch (error) {
    handlerFailed = true;
    handlerError = error;
  }

  let feedbackError: unknown;
  let feedbackFailed = false;
  if (feedbackContext) {
    try {
      await feedbackContext.flush();
    } catch (error) {
      feedbackFailed = true;
      feedbackError = error;
    }
  }
  if (handlerFailed) {
    throw handlerError;
  }
  if (feedbackFailed) {
    throw feedbackError;
  }
  return handlerResult;
}
